import { parseISO } from "date-fns";
import { ComplianceCaseTypeEnum, DocumentRefreshStatusEnum } from "~/graphql/supergraphEnums";
import { type GetComplianceCaseQuery, type ProfileType } from "~/graphql/supergraphTypes";
import { getRelationships } from "../Profiles/utils";
import { DOCUMENT_TYPE_MAPPING } from "./DocumentManagement/DocumentDetailsRightSidebar/constants";
import { DocumentTypeMappingValue } from "./DocumentManagement/DocumentDetailsRightSidebar/types";

type ExpiryStatus = DocumentRefreshStatusEnum.Expired | DocumentRefreshStatusEnum.ExpiringSoon;

const EXPIRING_SOON_DAYS = 30;

export const getExpiryStatus = (dateString: string | null | undefined): ExpiryStatus | null => {
  if (!dateString) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiry = parseISO(dateString);
  expiry.setHours(0, 0, 0, 0);

  if (expiry < today) return DocumentRefreshStatusEnum.Expired;

  const threshold = new Date(today);
  threshold.setDate(threshold.getDate() + EXPIRING_SOON_DAYS);

  if (expiry <= threshold) return DocumentRefreshStatusEnum.ExpiringSoon;

  return null;
};

// Function to find readableName by enumValue
export const findReadableNameByEnumValue = (enumValue: string): string | undefined => {
  const mappingValues = Object.values(DOCUMENT_TYPE_MAPPING) as DocumentTypeMappingValue[];
  const found = mappingValues.find((entry) => entry.enumValue === enumValue);
  return found ? found.readableName : undefined;
};

export const extractChildProfiles = (
  complianceCase: GetComplianceCaseQuery["complianceCase"] | undefined,
  picrRevampEnabled?: boolean,
) => {
  const all_profiles: ProfileType[] = [];
  if (
    picrRevampEnabled &&
    complianceCase?.caseType === ComplianceCaseTypeEnum.PaymentInstructionChangeReview
  ) {
    all_profiles.push(complianceCase.paymentAccount?.profile as ProfileType);
    return all_profiles;
  }
  if (complianceCase) {
    const investing_profile = complianceCase?.investingProfile;
    all_profiles.push(investing_profile as ProfileType);
    complianceCase?.profiles.map((profile) => all_profiles.push(profile as ProfileType));
  }
  return all_profiles;
};

export const deDuplicateDocuments = <T extends { id: number }>(docs1: T[], docs2: T[]): T[] => {
  const seenIds = new Set<number>();
  const result: T[] = [];
  for (const doc of [...docs1, ...docs2]) {
    if (!seenIds.has(doc.id)) {
      seenIds.add(doc.id);
      result.push(doc);
    }
  }
  return result;
};

export const hasEscalationFilters = (filters: string): boolean => {
  if (!filters) return false;
  const params = new URLSearchParams(filters);
  return params.has("sourceCaseId") || params.has("clientId");
};

export const getProfileRolesByProfileId = (
  profileId: number | null,
  allProfiles: ProfileType[],
) => {
  const profile = allProfiles.find((p) => p?.id === profileId);
  if (!profile?.parentRelationships) {
    return "Root profile";
  }
  return getRelationships(profile?.parentRelationships);
};
